import { HttpMethod } from '@aibees/shared/definition';
import { useSnackbarProgressStore } from '@aibees/shared/store';
import { useMutation } from '@tanstack/react-query';
import axios, { AxiosRequestConfig } from 'axios';
import { random } from 'lodash-es';
import posthog from 'posthog-js';
import { useRef } from 'react';
import { IconType } from '../components/icon';
import { notificationIconMap } from '../features/notifications/config';
import { useNotify } from './use-notify';

type DownloadVariables = {
  url: string;
  filename?: string;
  extension?: string;
};

/**
 * Trigger a GET download request and save it with the provided filename.
 * The hook relies on the server to stream the file; the client only creates
 * a Blob URL once the response arrives.
 */
export const useDownloadMutation = (filename: string, extension?: string) => {
  const notify = useNotify();
  const { setProgress, removeProgress } = useSnackbarProgressStore();
  const snackbarKeyRef = useRef<string | number | undefined>();
  const lastProgressRef = useRef(0);

  const requestConfig: AxiosRequestConfig<never> = {
    method: HttpMethod.get,
    responseType: 'blob',
    onDownloadProgress: (evt) => {
      if (evt.total) {
        const progress = Math.round((evt.loaded / evt.total) * 100);
        // Only update if progress changed. Skip no-op updates to reduce render churn.
        if (progress !== lastProgressRef.current) {
          // If this is the first progress update, enqueue the snackbar
          if (!snackbarKeyRef.current) {
            const key = notify.progress(
              'Downloading file in progress',
              ` <b>${filename}${!extension ? '' : '.'}${extension}</b> `,
              `download-progress-${filename + random(1, 100)}`,
              {
                icon: notificationIconMap.Download as IconType,
                persist: true,
                autoHideDuration: null,
                preventDuplicate: true,
              }
            );
            snackbarKeyRef.current = key;
            // Store initial progress
            setProgress(key, progress);
          } else {
            // Update progress in the store - the component will read from it
            setProgress(snackbarKeyRef.current, progress);
          }
          lastProgressRef.current = progress;
        }
      }
    },
  };

  const mutation = useMutation<Blob, unknown, DownloadVariables>({
    mutationKey: [useDownloadMutation.name],
    mutationFn: async (variables) => {
      // Some signed URLs come already URL-encoded. Decode to avoid double-encoding
      // (e.g. X-Amz-Credential containing `%2F` should be `/` for AWS).
      const url = decodeURIComponent(variables.url);
      const response = await axios({
        ...requestConfig,
        url,
      });
      return response.data;
    },
    onMutate: () => {
      lastProgressRef.current = 0;
    },
    onSuccess: (data, variables) => {
      const ext = extension ?? variables.extension ?? 'csv';
      const file = filename ?? variables.filename ?? 'download';
      const blobUrl = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${file}.${ext}`;
      link.click();
      URL.revokeObjectURL(blobUrl);

      // Ensure progress is set to 100% and wait 2 seconds before closing
      if (snackbarKeyRef.current) {
        setProgress(snackbarKeyRef.current, 100);
        // Wait 2 seconds before closing to make fast downloads visible
        setTimeout(() => {
          if (snackbarKeyRef.current) {
            removeProgress(snackbarKeyRef.current);
            notify.closeSnackbar(snackbarKeyRef.current);
            snackbarKeyRef.current = undefined;
            lastProgressRef.current = 0;
          }
        }, 2000);
      }
    },
    onSettled: (data, error) => {
      // Only handle cleanup here if it wasn't already handled in onSuccess
      // (onSuccess handles successful downloads with a delay)
      if (error && snackbarKeyRef.current) {
        removeProgress(snackbarKeyRef.current);
        notify.closeSnackbar(snackbarKeyRef.current);
        snackbarKeyRef.current = undefined;
        lastProgressRef.current = 0;
      }
    },
    onError: (error, variables) => {
      const isAxios = axios.isAxiosError(error);
      const status = isAxios ? error?.response?.status : undefined;
      const url = isAxios ? error?.config?.url : undefined;
      const message = error instanceof Error ? error?.message : String(error);

      // A network-layer failure (no HTTP response, e.g. ERR_NETWORK) means the blob
      // request was blocked or dropped before reaching the server — typically a content
      // blocker / privacy extension, VPN, corporate proxy or AV intercepting the XHR to
      // the cross-origin S3 host. A direct browser navigation isn't subject to CORS and
      // is usually not blocked by filters that only target XHR/fetch, so we fall back to
      // it. We only do this when there's no status — a real HTTP error (403 expired link,
      // 404, 5xx) would just navigate the user to an S3 error page.
      const fallbackUrl =
        url ?? (variables?.url ? decodeURIComponent(variables.url) : undefined);
      const shouldFallback = isAxios && !status && !!fallbackUrl;

      console.error('[useDownloadMutation] Download failed', {
        status,
        url,
        message,
        shouldFallback,
      });

      if (shouldFallback) {
        notify.info('Direct download started…');
      } else if (message) {
        notify.error(message);
      }

      posthog.capture(
        'download_mutation_failed',
        {
          status,
          url,
          message,
          filename,
          extension,
          errorName: error instanceof Error ? error?.name : undefined,
          stack: error instanceof Error ? error?.stack : undefined,
          axiosCode: isAxios ? error?.code : undefined,
          fallbackTriggered: shouldFallback,
        },
        { send_instantly: true }
      );

      if (shouldFallback && fallbackUrl) {
        window.location.assign(fallbackUrl);
      }
    },
  });

  return mutation;
};
